import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { RenovationDocument } from "shared/types/renovation";
import { toast } from "react-toastify";

/**
 * Filter parameters for renovation documents
 */
export interface RenovationDocumentsFilters {
  buildingId?: string;
  search?: string;
  documentType?: string[];
  documentStatus?: string[];
  projectId?: number;
  projectPartId?: number;
  supplierId?: number;
  year?: number;
  /** Odoo reconciliation filter: 'exists' or 'missing'. */
  odooStatus?: string;
  /** Odoo payment status: 'paid' | 'not_paid' | 'partial' | 'in_payment' | 'reversed'. */
  paymentStatus?: string;
}

/**
 * Convert filter object to API query string params.
 * Exported so the stats hook can reuse the same conversion logic.
 */
export function toApiParams(filters: RenovationDocumentsFilters): Record<string, string> {
  const params: Record<string, string> = {};

  if (filters.search) {
    params.search = filters.search;
  }
  if (filters.documentType?.length) {
    params.documentType = filters.documentType.join(",");
  }
  if (filters.documentStatus?.length) {
    // LET OP: de query-parameter heet `status`, niet `documentStatus`. De backend-DTO
    // (`GetPaginatedDocumentsDto.status`) draait met `whitelist: true`, dus een
    // parameter met een andere naam wordt er stil uitgefilterd — daardoor deed
    // filteren op status niets, terwijl filteren op type wél werkte.
    params.status = filters.documentStatus.join(",");
  }
  if (filters.projectId) {
    params.projectId = filters.projectId.toString();
  }
  if (filters.projectPartId) {
    params.projectPartId = filters.projectPartId.toString();
  }
  if (filters.supplierId) {
    params.supplierId = filters.supplierId.toString();
  }
  if (filters.year) {
    params.year = filters.year.toString();
  }
  if (filters.odooStatus) {
    params.odooStatus = filters.odooStatus;
  }
  if (filters.paymentStatus) {
    params.paymentStatus = filters.paymentStatus;
  }

  return params;
}

interface PaginatedResponse {
  data: RenovationDocument[];
  totalCount: number;
  hasMore: boolean;
}

const PAGE_SIZE = 30;

/**
 * Hook for fetching renovation documents with explicit page-based pagination.
 *
 * Replaces the previous useLazyLoading (append-mode) implementation.
 * All filtering, sorting and pagination remain 100% server-side.
 * Page resets to 1 whenever filterParams change.
 *
 * @param buildingId - The building ID to fetch documents for (empty = all buildings)
 * @param filters - Filter parameters for documents
 */
export const useRenovationDocumentsData = (
  buildingId: string,
  filters: RenovationDocumentsFilters = {}
) => {
  const { session } = useSession();

  const [documents, setDocuments] = useState<RenovationDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPageState] = useState(1);

  // Stable serialised key so effects only fire when filter values actually change.
  // IMPORTANT: buildingId is part of the endpoint PATH (not in filterParams), but it
  // still narrows the result set. It MUST be part of filterKey, otherwise changing
  // only the building filter does not trigger a refetch and the list keeps showing
  // the previously-fetched results while the stats/KPI cards (which key off the
  // endpoint) update correctly. See useRenovationDocumentsStats.ts:88-92.
  const filterParams = useMemo(() => toApiParams(filters), [filters]);
  const filterKey = useMemo(
    () => JSON.stringify({ ...filterParams, __buildingId: buildingId || "" }),
    [filterParams, buildingId]
  );

  // Track the filterKey from the last fetch so we know when to reset page
  const lastFilterKeyRef = useRef(filterKey);

  // Derive the paginated endpoint
  const endpoint = buildingId
    ? `${API_URL}/renovation/building/${buildingId}/documents/paginated`
    : `${API_URL}/renovation/documents/paginated`;

  // Any user-applied narrowing of the result set counts as an active filter,
  // INCLUDING the buildingId page scope.  Treating buildingId as "context only"
  // confused the UX: when ?building=X was set the "Gefilterd resultaat" card
  // showed "(geen filters actief)" even though the result set was clearly
  // narrowed by the building.
  const hasActiveFilters = useMemo(() => {
    return !!(
      buildingId ||
      filters.search ||
      filters.documentType?.length ||
      filters.documentStatus?.length ||
      filters.projectId ||
      filters.projectPartId ||
      filters.supplierId ||
      filters.year
    );
  }, [filters, buildingId]);

  // Public page setter — resets page like any other controlled setter
  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  /**
   * Core fetch — called whenever page or filterKey changes.
   */
  const fetchDocuments = useCallback(
    async (targetPage: number) => {
      if (!session) return;

      setIsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          ...filterParams,
          page: targetPage.toString(),
          pageSize: PAGE_SIZE.toString(),
          sortField: "id",
          sortDirection: "DESC",
        });

        const response = await fetchWithAuth(
          `${endpoint}?${queryParams}`,
          { method: "GET" },
          session
        );

        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Failed to fetch renovation documents: ${errorText}`);
        }

        const result: PaginatedResponse = await response.json();
        setDocuments(result.data ?? []);
        setTotalCount(result.totalCount ?? 0);
      } catch (error) {
        console.error("[useRenovationDocumentsData] Error fetching documents:", error);
        toast.error("Fout bij ophalen van documenten");
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [session, endpoint, filterParams]
  );

  // Reset page to 1 when filters change, then fetch page 1
  useEffect(() => {
    if (!session) return;

    if (filterKey !== lastFilterKeyRef.current) {
      lastFilterKeyRef.current = filterKey;
      setPageState(1);
      fetchDocuments(1);
    }
  }, [filterKey, session, fetchDocuments]);

  // Fetch whenever page changes (and filters are stable)
  useEffect(() => {
    if (!session) return;
    fetchDocuments(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, session]);

  // Also fetch on first mount when filterKey matches the ref (initial load)
  useEffect(() => {
    if (!session) return;
    fetchDocuments(1);
    // Run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  /**
   * Optimistically drop rows from the current page without waiting for a
   * refetch — used right after a (confirmed) delete so the documents disappear
   * instantly instead of lingering until the background resync completes.
   */
  const removeDocuments = useCallback((ids: number[]) => {
    if (!ids.length) return;
    const idSet = new Set(ids);
    setDocuments((prev) => prev.filter((d) => !idSet.has(d.id as number)));
    setTotalCount((prev) => Math.max(0, prev - ids.length));
  }, []);

  return {
    documents,
    isLoading,
    totalCount,
    page,
    pageSize: PAGE_SIZE,
    setPage,
    fetchDocuments: () => fetchDocuments(page),
    removeDocuments,
    hasActiveFilters,
  };
};
